import { useQuery } from "@tanstack/react-query";
import type { UseQueryResult } from "@tanstack/react-query";

import { API_ROUTES } from "../../api/ApiRoutes";
import type { ChallengeApiRecord, ChallengesListResponse } from "../../types/gamePass";
import { getPrivateInstance } from "../_privateInstance";
import { retryOnTransientErrors } from "./queryUtils";
import { unwrapGamePassCollection } from "./responseMappers";

/** Scope the challenges list to a season *or* a campaign. The BE reuses the
 *  same `GET /challenges` endpoint for both, keyed on `season_id` /
 *  `campaign_id` (see `ApiRoutes.js`), so exactly one of these should be set. */
export type ChallengesListQuery = {
  seasonId?: string;
  campaignId?: string;
};

const fetchChallengesList = async ({
  seasonId,
  campaignId,
}: ChallengesListQuery): Promise<ChallengesListResponse> => {
  const response =
    await getPrivateInstance().get<ChallengesListResponse>(
      API_ROUTES.GET_CHALLENGES_LIST,
      {
        params: {
          ...(seasonId ? { season_id: seasonId } : {}),
          ...(campaignId ? { campaign_id: campaignId } : {}),
        },
      },
    );
  return {
    ...response.data,
    challenges: unwrapGamePassCollection<ChallengeApiRecord>(
      response.data,
      "challenges",
    ),
  };
};

export const useGetChallengesList = (
  params?: ChallengesListQuery,
): UseQueryResult<
  ChallengesListResponse,
  Error
> =>
  useQuery<ChallengesListResponse, Error>({
    queryKey: [
      "game-pass",
      "challenges",
      "list",
      params?.seasonId ?? "",
      params?.campaignId ?? "",
    ],
    queryFn: () => fetchChallengesList(params as ChallengesListQuery),
    placeholderData: (previousData) => previousData,
    staleTime: 1000 * 30,
    refetchOnWindowFocus: false,
    retry: retryOnTransientErrors,
    enabled: Boolean(params?.seasonId || params?.campaignId),
  });

export { fetchChallengesList };
